import { ValidationError, InvalidTimeSlotError, MemberPermissionError } from './errors.js';

/**
 * Validates booking data
 * @param {Object} bookingData - Booking data to validate
 * @throws {ValidationError} When validation fails
 */
export function validateBooking(bookingData) {
  if (!bookingData) {
    throw new ValidationError('Booking data is required');
  }

  if (!bookingData.memberId) {
    throw new ValidationError('Member ID is required', 'memberId', bookingData.memberId);
  }

  if (!bookingData.rinkId) {
    throw new ValidationError('Rink ID is required', 'rinkId', bookingData.rinkId);
  }

  if (!bookingData.bookingTypeId) {
    throw new ValidationError('Booking type ID is required', 'bookingTypeId', bookingData.bookingTypeId);
  }

  if (!bookingData.date) {
    throw new ValidationError('Date is required', 'date', bookingData.date);
  }

  if (!bookingData.start || !(bookingData.start instanceof Date)) {
    throw new ValidationError('Valid start time is required', 'start', bookingData.start);
  }

  if (!bookingData.end || !(bookingData.end instanceof Date)) {
    throw new ValidationError('Valid end time is required', 'end', bookingData.end);
  }

  if (bookingData.start >= bookingData.end) {
    throw new ValidationError('End time must be after start time', 'time', {
      start: bookingData.start,
      end: bookingData.end
    });
  }

  // Validate status if provided
  const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed'];
  if (bookingData.status && !validStatuses.includes(bookingData.status)) {
    throw new ValidationError('Invalid booking status', 'status', bookingData.status);
  }
}

/**
 * Validates time slot data
 * @param {Object} timeSlot - Time slot to validate
 * @throws {InvalidTimeSlotError} When validation fails
 */
export function validateTimeSlot(timeSlot) {
  if (!timeSlot) {
    throw new InvalidTimeSlotError('Time slot is required');
  }

  if (!timeSlot.start || !(timeSlot.start instanceof Date)) {
    throw new InvalidTimeSlotError('Valid start time is required');
  }

  if (!timeSlot.end || !(timeSlot.end instanceof Date)) {
    throw new InvalidTimeSlotError('Valid end time is required');
  }

  if (timeSlot.start >= timeSlot.end) {
    throw new InvalidTimeSlotError('End time must be after start time');
  }
}

/**
 * Validates member permissions (placeholder for future implementation)
 * @param {string} memberId - Member ID to validate
 * @param {string} action - Action being performed
 * @throws {MemberPermissionError} When validation fails
 */
export function validateMember(memberId, action = 'access') {
  if (!memberId || typeof memberId !== 'string') {
    throw new MemberPermissionError('Valid member ID is required', memberId);
  }

  // Placeholder for future permission validation logic
  // This would integrate with the container app's permission system
}

/**
 * Validates UUID format
 * @param {string} id - ID to validate
 * @param {string} fieldName - Name of the field being validated
 * @throws {ValidationError} When validation fails
 */
export function validateUUID(id, fieldName = 'id') {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  
  if (!id || typeof id !== 'string' || !uuidRegex.test(id)) {
    throw new ValidationError(`Valid UUID required for ${fieldName}`, fieldName, id);
  }
}

/**
 * Validates color hex format
 * @param {string} color - Color hex string to validate
 * @param {string} fieldName - Name of the field being validated
 * @throws {ValidationError} When validation fails
 */
export function validateColor(color, fieldName = 'color') {
  const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  
  if (!color || typeof color !== 'string' || !colorRegex.test(color)) {
    throw new ValidationError(`Valid hex color required for ${fieldName}`, fieldName, color);
  }
} 